// src/encounter/ips-condition-dialog.tsx
// Add/Edit dialog for the IPS-rail Active/Resolved Problems sections. PRESENTATIONAL —
// composes DS Modal + CodeSearchField (ICD-10 diagnosis) + Select (clinicalStatus
// active/resolved) + DatePicker (resolution date, shown only when Resolved) +
// Button. No hooks/network: the host supplies search results + the submit/search callbacks
// (mirrors FlagAddDialog / IpsAllergyDialog). On submit it resolves the selection into a
// ConditionFormValue; the rail turns that into a wire request via buildConditionRequest +
// useSaveCondition. active↔resolved is the clinicalStatus select.
import { useState } from 'react';
import {
  Button,
  Select,
  DatePicker,
  TextField,
  CodeSearchField,
  HelperText,
  type CodeSearchResult,
  type SelectOption,
} from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import { ReportedDateField } from './reported-date-field';
import { resolveReportedDate, seedReportedDate } from './lib/reported-date';
import { CONDITION_STATUS, type ConditionCode, type PatientCondition } from '@/types/patient-chart-api';
import type { ConditionFormValue } from '@/encounter/lib/to-condition-request';

const STATUS_OPTIONS: SelectOption<'Active' | 'Resolved'>[] = [
  { value: CONDITION_STATUS.active, label: 'Active' },
  { value: CONDITION_STATUS.resolved, label: 'Resolved' },
];

export interface IpsConditionDialogProps {
  open: boolean;
  /** Present → Edit mode; absent → Add mode. */
  editing?: PatientCondition | null;

  // Diagnosis search (host-owned).
  query: string;
  onQueryChange: (next: string) => void;
  results: CodeSearchResult[];
  searching?: boolean;
  /** Resolve a clicked result id → the full ICD-10 ConditionCode row. */
  resolveCode: (result: CodeSearchResult) => ConditionCode | null;

  saving?: boolean;
  onSubmit: (value: ConditionFormValue) => void;
  onClose: () => void;
}

/** THE ruled field-label treatment (PROD-1801, Andrew 2 Aug) — shared, not re-declared.
 *  These six dialogs each carried a byte-identical local copy at xs/600/0.08em, which is a
 *  seventh idiom next to the four the rail popover already showed at once. The margin is the
 *  only local part: `AP_FIELD_LABEL` sets no spacing, and these labels sit above their field. */
// NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass), so its
// literal values (12/700/uppercase/0.01em/--color-text-hint) are reproduced here as the
// equivalent class string rather than imported as a style object.
const FIELD_LABEL =
  'pa-ips-label block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const FIELD_GROUP = 'mb-4';
const SELECTED_CHIP =
  'pa-ips-chip flex items-center justify-between gap-2 py-2 px-3 border border-solid border-border-default rounded-md bg-surface-card';
/** Local-date YYYY-MM-DD (no UTC shift) — matches conditions.ts local-date convention. */
function toYyyymmdd(d: Date | undefined): string | null {
  if (!d || Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Outer gate + keyed remount; the inner form initialises state from `editing` via lazy
 *  useState initialisers (no setState-in-effect). */
export function IpsConditionDialog(props: IpsConditionDialogProps) {
  if (!props.open) return null;
  return <IpsConditionDialogForm key={props.editing?.identifier ?? 'add'} {...props} />;
}

function IpsConditionDialogForm({
  editing = null,
  query,
  onQueryChange,
  results,
  searching,
  resolveCode,
  saving,
  onSubmit,
  onClose,
}: IpsConditionDialogProps) {
  const isEditing = !!editing?.identifier;
  const [code, setCode] = useState<ConditionCode | null>(() => editing?.code ?? null);
  // Add always creates an ACTIVE problem (the CreateCondition contract is always-Active; a
  // resolved problem is reached by ADDing then resolving via Edit). The Active↔Resolved toggle
  // therefore renders only in Edit mode.
  const [status, setStatus] = useState<'Active' | 'Resolved'>(() =>
    editing?.clinicalStatus === CONDITION_STATUS.resolved ? 'Resolved' : 'Active',
  );
  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    editing?.endDate ? new Date(editing.endDate) : undefined,
  );
  // Fields Flutter collects that the rail dialog never did (PROD-1994):
  //   Onset date  — `problems_tab.dart:565`, a DynamicDatePicker, so "3y" is a valid entry
  //   Description — Flutter's TextField on the same form, wire field `note`
  // Both already existed on ConditionFormValue and on the wire; the dialog hardcoded them to the
  // existing value on edit and null on add, so a problem added from the rail lost both.
  // Onset may be a calendar date, a duration (stored at year precision) or an explicit Unknown
  // (encounter owner answers on PR #843, 1 Nov 2026) — see `lib/reported-date.ts`.
  const [onsetDate, setOnsetDate] = useState(() =>
    seedReportedDate(editing?.onsetDisplayDateTime, editing?.onsetDateTime),
  );
  const [note, setNote] = useState<string>(() => editing?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    if (!code) {
      setError('Pick a diagnosis.');
      return;
    }
    const endYyyymmdd = status === 'Resolved' ? toYyyymmdd(endDate) : null;
    if (status === 'Resolved' && !endYyyymmdd) {
      setError('Pick a resolution date.');
      return;
    }
    // Onset is OPTIONAL in Flutter (not mandatory), but text that does not parse is an error rather
    // than an omission — otherwise the typed value is silently discarded.
    const onset = resolveReportedDate(onsetDate);
    if (!onset.ok) {
      setError('That onset date could not be read. Try DD/MM/YYYY, MM/YYYY, YYYY, or a duration like 3Y.');
      return;
    }
    onSubmit({
      code,
      clinicalStatus: status,
      onsetDateTime: onset.iso,
      onsetDisplayDateTime: onset.display,
      note: note.trim() || null,
      endYyyymmdd,
    });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit problem' : 'Add problem'}
      size="md"
      commit={{
        run: handleSubmit,
        label: 'Save problem',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        {/* Diagnosis */}
        <div className={FIELD_GROUP}>
          <span className={FIELD_LABEL}>Diagnosis</span>
          {code ? (
            <div className={SELECTED_CHIP}>
              <span className="text-size-sm text-text-primary">
                {code.display ?? code.code ?? '—'}
                {code.code ? (
                  <span className="text-text-hint"> · {code.code}</span>
                ) : null}
              </span>
              <Button variant="tertiary" size="sm" onClick={() => setCode(null)} disabled={saving}>
                Change
              </Button>
            </div>
          ) : (
            <CodeSearchField
              value={query}
              onChange={onQueryChange}
              results={results}
              loading={searching}
              placeholder="Search ICD-10 diagnoses…"
              onResultClick={(r) => {
                const picked = resolveCode(r);
                if (picked) {
                  setCode(picked);
                  setError(null);
                }
              }}
            />
          )}
        </div>

        {/* Status (active/resolved) — Edit only. On Add the problem is always created Active. */}
        {isEditing ? (
          <div className={FIELD_GROUP}>
            <Select label="Status" options={STATUS_OPTIONS} value={status} onChange={setStatus} disabled={saving} />
          </div>
        ) : null}

        {/* Resolution date — only when editing → Resolved */}
        {isEditing && status === 'Resolved' ? (
          <div className={FIELD_GROUP}>
            {/* TODO(PROD-1993): this stays a full-date DS DatePicker while every sibling IPS date field
                moved to the duration-capable DynamicDateInput under PROD-1266. Flutter's condition
                dates ARE free-text (`problems_tab.dart:565` onset, `:890,975` end), so this is a known
                gap, not the intended shape. It was excluded deliberately: unlike the other surfaces,
                ConditionFormValue carries a bare `endYyyymmdd` whose display partner is DERIVED, so
                the swap means reshaping it through eight call sites — one of them the one-click
                `resolve` action that PROD-1266 was concurrently changing to add `unresolve`.
                PROD-1993 also covered the MISSING onset field (this dialog never collected one) and an
                invented Severity select (Flutter hardcodes severity and has no UI for it). Severity
                is now gone: the encounter owner ruling of 1 Nov 2026 drops it from the Problem form. */}
            <DatePicker label="Resolution date" value={endDate} onChange={setEndDate} maxDate={new Date()} disabled={saving} />
          </div>
        ) : null}

        {/* Onset — a date, a duration (stored at year precision) or Unknown. Optional. */}
        <div className={FIELD_GROUP}>
          <ReportedDateField label="Onset date" value={onsetDate} onChange={setOnsetDate} disabled={saving} />
        </div>

        {/* Description — Flutter's TextField on the same form; wire field `note`. */}
        <div className={FIELD_GROUP}>
          <TextField
            label="Description"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional context…"
            disabled={saving}
          />
        </div>

        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
